import { useCallback, useMemo, useState, useEffect } from "react";
import useGetReportContactDetails from "hooks/organization/reports/types/useGetReportContactDetails";
import { capitalCase } from "change-case";
import JobModal from "components/JobModal/JobModal";
import usePaginatedResources from "hooks/usePaginatedResources";

export default function useReportTableContactDetails(companyId = null) {
  const [openModal, setOpenModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const [getReportContactDetails, { data, loading, error }] = useGetReportContactDetails(false); // No success message for filtering/pagination

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const paginate = useCallback(
    ({ page = 1, first = 25 } = {}) => {
      const variables = {
        companyId,
        page,
        first,
      };
      getReportContactDetails({ variables });
    },
    [companyId, getReportContactDetails]
  );

  const pagination = usePaginatedResources({
    paginate,
    paginatorInfo: data?.getReportContactDetails?.paginatorInfo,
  });

  const columns = useMemo(() => {
    return [
      {
        Header: "ID",
        accessor: "id",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Company Name",
        accessor: "name",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Phone",
        accessor: "phone",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ value }) => value || "",
        filter: "includes",
        disableSortBy: true,
      },
      {
        Header: "Contact First Name",
        accessor: "first_name",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Contact Last Name",
        accessor: "last_name",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Contact Email",
        accessor: "contact_email",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Contact Phone",
        accessor: "contact_phone",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      // Additional columns available but hidden by default
      {
        Header: "Address Line 1",
        accessor: "address_line_1",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Address Line 2",
        accessor: "address_line_2",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "City",
        accessor: "address_city",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "State",
        accessor: "address_state",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Zip",
        accessor: "address_zip",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Country",
        accessor: "address_country",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Website",
        accessor: "website",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Notes",
        accessor: "notes",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Contact Title",
        accessor: "title",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Contact Role",
        accessor: "role",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
      {
        Header: "Company ID",
        accessor: "company_id",
        Cell: ({ value }) => value || "",
        disableSortBy: true,
      },
    ];
  }, [handleOpenModal]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data?.getReportContactDetails?.data || [],
      modal: <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />,
    };
  }, [columns, data, selectedJobId, openModal, handleCloseModal]);

  return {
    columns,
    data,
    loading,
    error,
    tableData,
    pagination,
    getReportContactDetails: paginate,
  } as const;
}
